import { UserFacingError, getErrorMessage } from '@features/shared/api/client'
import { useCallback, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useToast } from '@features/shared/components/Toast/ToastContext'
import { useStableCallback } from '@features/shared/hooks/useStableCallback'
import { OWN_PREFERENCE_SAVE_DELAY_MS } from '@/styling/tokens.interaction'
import { settingsQueryKeys } from '../api/queryKeys'
import { getUserPreferences, updateUserPreferences } from '../api/userPreferences.api'
import { findPreferenceByKey } from '../logic/preferences'
import { valuesEqual } from '../logic/preferenceValues'
import { usePreferenceEditingStore } from '../stores/usePreferenceEditingStore'
import { useSettingsStore, type EffectiveSettings } from '../stores/useSettingsStore'
import { broadcastSettingsResolutionChanged } from '../sync/settingsResolutionSync'
import type { ResolvedConfiguration } from '../models'

const USER_PREFERENCES_STALE_MS = 5 * 60 * 1000

interface PendingChange {
  settingKey: keyof EffectiveSettings
  value: unknown
  /** The value before the first unsaved change, to put back if saving fails. */
  savedValue: unknown
}

/**
 * Saves the signed-in user's own Preferences from outside the Preferences page.
 * A change shows at once; quick successive changes save together in one request
 * once the clicks stop. A failed or organization-locked save puts the value back.
 */
export function useUpdateOwnPreference() {
  const queryClient = useQueryClient()
  const { addToast } = useToast()
  const pendingRef = useRef(new Map<string, PendingChange>())
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const savingRef = useRef(false)

  const rollBack = (preferenceKey: string, change: PendingChange) => {
    // A newer change to the same Preference is still waiting; it wins.
    if (pendingRef.current.has(preferenceKey)) return
    useSettingsStore.getState().applyEffectiveSettings({
      [change.settingKey]: change.savedValue,
    } as Partial<EffectiveSettings>)
  }

  const flush = useStableCallback(async () => {
    timerRef.current = null
    // One save at a time; changes made meanwhile go in the next one.
    if (savingRef.current || pendingRef.current.size === 0) return
    const changes = [...pendingRef.current]
    pendingRef.current.clear()
    savingRef.current = true

    try {
      const userId = useSettingsStore.getState().appUserId
      const preferences = await queryClient.fetchQuery({
        queryKey: settingsQueryKeys.userPreferences,
        queryFn: getUserPreferences,
        staleTime: USER_PREFERENCES_STALE_MS,
      })
      if (!userId) throw new UserFacingError('Your preferences are not available yet.')

      const updates = []
      const savedValues: Record<string, unknown> = {}
      for (const [preferenceKey, change] of changes) {
        const preference = findPreferenceByKey(preferences.tabs, preferenceKey)
        if (!preference || !preference.overrides_allowed) {
          rollBack(preferenceKey, change)
          addToast({
            type: 'error',
            title: "Couldn't save preference",
            message: preference
              ? 'Your organization sets this preference.'
              : 'This preference is not available yet.',
          })
          continue
        }
        updates.push({
          preference_id: preference.preference_id,
          assignee: { type: 'user' as const, id: userId },
          // Choosing the default follows it (null) rather than copying it.
          value: valuesEqual(change.value, preference.inherited_value) ? null : change.value,
        })
        savedValues[preferenceKey] = change.value
      }
      if (updates.length === 0) return

      const saved = await updateUserPreferences({ user_id: userId, updates })
      queryClient.setQueryData(settingsQueryKeys.userPreferences, saved)
      // Unsaved edits open on My Preferences stay, on top of what this saved.
      usePreferenceEditingStore.getState().hydrateUserPreferences(saved)

      // The configuration the app started from now says what was saved; values
      // still waiting to save stay as shown rather than flicking back.
      const waiting = Object.fromEntries(
        [...pendingRef.current].map(([preferenceKey, change]) => [preferenceKey, change.value])
      )
      queryClient.setQueryData<ResolvedConfiguration>(
        settingsQueryKeys.configuration,
        (configuration) =>
          configuration && {
            ...configuration,
            preferences: { ...configuration.preferences, ...savedValues, ...waiting },
          }
      )
      broadcastSettingsResolutionChanged()
    } catch (error) {
      for (const [preferenceKey, change] of changes) rollBack(preferenceKey, change)
      addToast({
        type: 'error',
        title: "Couldn't save preference",
        message: getErrorMessage(error),
      })
    } finally {
      savingRef.current = false
      if (pendingRef.current.size > 0 && timerRef.current === null) {
        timerRef.current = setTimeout(() => void flush(), OWN_PREFERENCE_SAVE_DELAY_MS)
      }
    }
  })

  // Unsaved changes still save when the sidebar unmounts (switching layouts).
  useEffect(
    () => () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
        void flush()
      }
    },
    [flush]
  )

  return useCallback(
    <K extends keyof EffectiveSettings>(
      preferenceKey: string,
      settingKey: K,
      value: EffectiveSettings[K]
    ) => {
      const store = useSettingsStore.getState()
      const earlier = pendingRef.current.get(preferenceKey)
      pendingRef.current.set(preferenceKey, {
        settingKey,
        value,
        savedValue: earlier ? earlier.savedValue : store[settingKey],
      })
      store.applyEffectiveSettings({ [settingKey]: value } as Partial<EffectiveSettings>)

      if (timerRef.current !== null) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => void flush(), OWN_PREFERENCE_SAVE_DELAY_MS)
    },
    [flush]
  )
}
